/**
 * Certification screens: shared access hook, labels and colours.
 *
 * WHAT THE USER MAY DO COMES FROM THE SERVER, NEVER FROM ROLE NAMES. Every
 * button and menu item asks `can(...)` against the capability list that
 * GET /certs/access returns. Widening access is a one-line change to the verb
 * table in api/app/certs_policy.py and appears here with no front-end edit.
 * The API re-checks every call regardless; this only decides what to offer.
 */

import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import type {
  CertAccess,
  CertCapability,
  CertRequirementOutcomeValue,
  CertState,
  ExpiryRule,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'

// One request per signed-in person per page load. Keyed on the employee so
// signing out and in as somebody else asks again.
// `value` holds the answer once it has arrived, so a screen mounted later
// starts loaded instead of spending a render in "not allowed".
let cached: { employeeId: number; promise: Promise<CertAccess>; value?: CertAccess } | null = null

function load(employeeId: number): Promise<CertAccess> {
  if (cached?.employeeId !== employeeId) {
    const entry: { employeeId: number; promise: Promise<CertAccess>; value?: CertAccess } = {
      employeeId,
      // Any failure reads as "off": a menu item that cannot be explained is
      // worse than one that is not there.
      promise: api
        .certAccess()
        .catch(() => ({ enabled: false, capabilities: [] as CertCapability[] }))
        .then((a) => {
          entry.value = a
          return a
        }),
    }
    cached = entry
  }
  return cached.promise
}

export interface CertAccessState {
  loaded: boolean
  enabled: boolean
  can: (c: CertCapability) => boolean
}

export function useCertAccess(): CertAccessState {
  const { user } = useAuth()
  const [access, setAccess] = useState<CertAccess | null>(() =>
    user && cached?.employeeId === user.employee_id ? cached.value ?? null : null,
  )

  useEffect(() => {
    if (!user) {
      setAccess(null)
      return
    }
    let cancelled = false
    void load(user.employee_id).then((a) => {
      if (!cancelled) setAccess(a)
    })
    return () => {
      cancelled = true
    }
  }, [user])

  const caps = new Set(access?.capabilities ?? [])
  return {
    loaded: access !== null,
    enabled: access?.enabled ?? false,
    can: (c) => caps.has(c),
  }
}

// --- Labels ---------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 2027-03-14 -> "14 Mar 2027". Parsed by hand: `new Date('2027-03-14')` is
 *  UTC midnight, which renders as the 13th in Kent. */
export function dayLabel(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return `${d} ${MONTHS[m - 1]} ${y}`
}

/** Plain-English state, as the matrix legend and the person page say it. */
export const STATE_LABEL: Record<CertState, string> = {
  Valid: 'Current',
  Expiring: 'Expiring soon',
  Expired: 'Expired',
  BlockedByPrereq: 'Blocked by a prerequisite',
  Reevaluate: 'Re-evaluation required',
  Pending: 'Awaiting verification',
}

export const OUTCOME_LABEL: Record<CertRequirementOutcomeValue, string> = {
  Met: 'Met',
  MetByEquivalent: 'Met by an equivalent',
  Expiring: 'Expiring soon',
  Blocked: 'Blocked',
  Expired: 'Expired',
  InGracePeriod: 'New hire — in grace period',
  Missing: 'Missing',
}

export const EXPIRY_RULE_LABEL: Record<ExpiryRule, string> = {
  FixedMonths: 'Fixed period from issue',
  CardDate: 'Date on the card',
  None: 'Does not expire',
}

type Tone = 'ok' | 'warn' | 'error' | 'info' | 'none'

export function stateTone(s: CertState | null | undefined): Tone {
  switch (s) {
    case 'Valid':
      return 'ok'
    case 'Expiring':
    case 'Pending':
      return 'warn'
    case 'Expired':
    case 'BlockedByPrereq':
    case 'Reevaluate':
      return 'error'
    default:
      return 'none'
  }
}

export function outcomeTone(o: CertRequirementOutcomeValue | null | undefined): Tone {
  switch (o) {
    case 'Met':
    case 'MetByEquivalent':
      return 'ok'
    case 'Expiring':
    case 'InGracePeriod':
      return 'warn'
    case 'Missing':
    case 'Expired':
    case 'Blocked':
      return 'error'
    default:
      return 'none'
  }
}

export function badgeClass(t: Tone): string {
  return t === 'none' ? 'badge' : `badge badge-${t}`
}

/** "Good until 14 Mar 2027" / "Expired 3 Oct 2026" / "No expiry". */
export function expiryPhrase(state: CertState | null, expires: string | null, redacted: boolean): string {
  if (redacted) return state ? STATE_LABEL[state] : '—'
  if (!expires) return 'No expiry'
  if (state === 'Expired') return `Expired ${dayLabel(expires)}`
  return `Good until ${dayLabel(expires)}`
}
